import React, { useState, useEffect, useRef } from 'react';
import { Plus, Users, Activity, X, Download, Grid, BarChart2 } from 'lucide-react';
import Layout from '../pages/Dashboard/DashboarEntreprise/Layout';
import ApplicationsList from './applications/ApplicationList';
import ApplicationForm from './applications/ApplicationForm';
import AddAdminModal from './applications/AddAdminModal';
import { createApplication } from '../api/ApiEntreprise';
import { mutate } from 'swr';
import { useApplications } from '../pages/Dashboard/DashboarEntreprise/hooksUseretApplication/useApplications';
import useApplicationUsers from '../pages/Dashboard/DashboarEntreprise/hooksUseretApplication/useApplicationUsers';
import UsersDistributionChart from './UsersDistributionChart';
import { useTranslation } from '../../TranslationContext';
import Navbar0 from '../../vitrine/Components/Navbar';

// URL de l'API depuis les variables d'environnement
const API_BASE_URL = import.meta.env.VITE_API;

/**
 * Composant de compteur animé pour les statistiques
 * @param value - Valeur finale du compteur
 * @param duration - Durée de l'animation en ms
 */
const AnimatedCounter = ({ value, duration = 1000 }: { value: number, duration?: number }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(count);
  const previousValue = useRef(0);
  
  useEffect(() => {
    if (value < previousValue.current) {
      setCount(0);
    }
    
    previousValue.current = value;
    countRef.current = count;
    
    if (value === 0) {
      setCount(0);
      return;
    }
    
    const startTime = Date.now();
    const endValue = value;
    const startValue = countRef.current;
    
    const counter = () => {
      const elapsedTime = Date.now() - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      const easeOutQuart = (x: number): number => 1 - Math.pow(1 - x, 4);
      const easedProgress = easeOutQuart(progress);
      
      const currentValue = Math.floor(startValue + (endValue - startValue) * easedProgress);
      setCount(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(counter);
      } else {
        setCount(endValue);
      }
    };
    
    requestAnimationFrame(counter);
  }, [value, duration]);
  
  return <>{count}</>;
};

/**
 * Composant principal du tableau de bord d'entreprise
 * Gère les applications, administrateurs et utilisateurs
 */
const EntrepriseDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdminModalOpen, setAdminModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const company = localStorage.getItem('company');
  const companyId = company ? parseInt(company, 10) : null;

  const { applications, isLoading: isApplicationsLoading } = useApplications(companyId || 0);
  const { users, usersByApplication, isLoading: isUsersLoading } = useApplicationUsers(companyId || 0, applications);
  // const { users, usersByApplication } = useApplicationUsers(companyId || 0, applications);
  // Forcer la revalidation des données lors du montage
  useEffect(() => {
    if (companyId) {
      mutate(`${API_BASE_URL}/Company/${companyId}/applications`, null, { revalidate: true });
    }
  }, [companyId]);

  // Gérer l'absence de companyId
  if (!companyId) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-700">{t('failed_to_create_application')}</p>
      </div>
    );
  }

  // Afficher un spinner pendant le chargement
  // if (isApplicationsLoading) {
    if (isApplicationsLoading ||isUsersLoading ) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <svg
          className="animate-spin h-8 w-8 text-slate-700"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  const handleAdminCreated = (admin: any) => {
    console.log('Admin created:', admin);
    setSuccessMessage(t('administrator_added_successfully'));
  };

  const getChartData = () => {
    return applications
      .filter(app => usersByApplication[app.id] > 0)
      .map(app => ({
        name: app.displayName,
        value: usersByApplication[app.id] || 0,
        id: app.id
      }));
  };

  const handleExportCSV = () => {
    const chartData = getChartData();
    if (chartData.length === 0) {
      setErrorMessage(t('no_data_to_export'));
      return;
    }

    const headers = [t('application_name'), t('user_count')];
    const csvContent = [
      headers.join(','),
      ...chartData.map(item => `"${item.name}",${item.value}`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `users_distribution_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSuccessMessage(t('data_exported_successfully'));
  };

  return (
    <div>
      <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <Navbar0 />
      </div>
      <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
        {/* Sidebar */}
        <div className="w-full md:w-1/4 lg:w-1/5 bg-white shadow-md z-10">
          <Layout />
        </div>
        
        {/* Contenu principal */}
        <div className="w-full md:w-3/4 mt-20 lg:w-4/5 p-6 md:p-8 space-y-6">
          {/* Messages de notification */}
          {successMessage && (
            <div className="fixed top-4 right-4 bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded shadow-md animate-fade-in-out max-w-md z-50 flex items-center">
              <div className="mr-3 flex-shrink-0">
                <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <p>{successMessage}</p>
              <button 
                onClick={() => setSuccessMessage(null)} 
                className="ml-auto text-green-700 hover:text-green-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          
          {errorMessage && (
            <div className="fixed top-4 right-4 bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md animate-fade-in-out max-w-md z-50 flex items-center">
              <div className="mr-3 flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <p>{errorMessage}</p>
              <button 
                onClick={() => setErrorMessage(null)} 
                className="ml-auto text-red-700 hover:text-red-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* En-tête avec actions */}
          <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h1 className="text-2xl font-semibold text-gray-800 flex items-center">
                <Grid className="mr-2 h-6 w-6 text-gray-600" />
                {t('applications_management')}
              </h1>
              <div className="flex flex-col sm:flex-row gap-3">
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="flex items-center px-4 py-2 bg-slate-700 text-white rounded-lg transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 shadow-sm"
                >
                  <Plus className="w-4 h-4 mr-2" /> {t('new_application')}
                </button>
                <button 
                  onClick={() => setAdminModalOpen(true)}
                  className="flex items-center px-4 py-2 bg-slate-600 text-white rounded-lg transition-colors hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 shadow-sm"
                >
                  <Users className="w-4 h-4 mr-2" /> {t('add_administrator')}
                </button>
              </div>
            </div>
          </div>

          {/* Cartes statistiques */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
              <div className="flex items-center">
                <div className="flex-shrink-0 rounded-lg bg-red-100 p-3">
                  <Activity className="h-6 w-6 text-red-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500">{t('total_applications')}</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    <AnimatedCounter value={applications.length} duration={1200} />
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
              <div className="flex items-center">
                <div className="flex-shrink-0 rounded-lg bg-yellow-100 p-3">
                  <Users className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500">{t('total_users')}</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    <AnimatedCounter value={users} duration={1200} />
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 transition-all duration-300 hover:shadow-md">
              <div className="flex items-center">
                <div className="flex-shrink-0 rounded-lg bg-blue-100 p-3">
                  <BarChart2 className="h-6 w-6 text-blue-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <p className="text-sm font-medium text-gray-500">{t('average_users_per_app')}</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    <AnimatedCounter 
                      value={users > 0 ? Math.round((users / applications.length) * 100) / 100 : 0} 
                      duration={1200} 
                    />
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Liste des applications */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-medium text-gray-800 flex items-center">
                  <Grid className="mr-2 h-5 w-5 text-gray-600" />
                  {t('applications')}
                </h2>
              </div>
              <ApplicationsList companyId={companyId} />
            </div>
          </div>
          
          {/* Graphique de distribution */}
          <div className="bg-white shadow-sm rounded-xl p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-medium text-gray-800 flex items-center">
                <BarChart2 className="mr-2 h-5 w-5 text-gray-600" />
                {t('user_distribution_by_application')}
              </h2>
              <button 
                onClick={handleExportCSV}
                className="flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg transition-colors hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                disabled={getChartData().length === 0}
              >
                <Download className="w-4 h-4 mr-1" /> {t('export_csv')}
              </button>
            </div>
            <UsersDistributionChart 
              chartData={getChartData()} 
              isLoading={isApplicationsLoading || isUsersLoading}
              // isLoading={isApplicationsLoading }
            />
          </div>

          {/* Modal pour ajouter une nouvelle application */}
          {isModalOpen && (
            <div className="fixed inset-0 backdrop-blur-sm bg-black/30 flex items-center justify-center z-50 p-4 animate-fade-in">
              <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl transform transition-all animate-scale-in">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <Plus className="mr-2 h-5 w-5 text-gray-600" />
                    {t('add_new_application')}
                  </h2>
                  <button 
                    onClick={() => setIsModalOpen(false)} 
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <ApplicationForm 
                  companyId={companyId.toString()}
                  onSubmit={async (data: FormData) => {
                    try {
                      await createApplication(data);
                      mutate(`${API_BASE_URL}/Company/${companyId}/applications`);
                      setIsModalOpen(false);
                      setSuccessMessage(t('application_created_successfully'));
                    } catch (error) {
                      console.error("Error creating application:", error);
                      setErrorMessage(t('failed_to_create_application'));
                    }
                    setIsModalOpen(false);
                  }} 
                />
              </div>
            </div>
          )}

          {/* Modal pour ajouter un administrateur */}
          <AddAdminModal 
            isOpen={isAdminModalOpen} 
            onClose={() => setAdminModalOpen(false)}
            onAdminCreated={handleAdminCreated}
          />
        </div>
      </div>
    </div>
  );
};

export default EntrepriseDashboard;
